<template>
  <div>
    <b-row>
      <b-col cols="6">
        <b-form-datepicker
          v-model="fromDate"
          class="mb-2"
          placeholder="Date de debut"
          label-no-date-selected="Date de debut"
        ></b-form-datepicker>
      </b-col>
      <b-col cols="6">
        <b-form-datepicker
          v-model="toDate"
          class="mb-2"
          placeholder="Date de fin"
          label-no-date-selected="Date de fin"
        ></b-form-datepicker>
      </b-col>
    </b-row>
    <CustomTable
      class="p-0 text-left"
      :items="items"
      :fields="fields"
      :filterable="true"
      :selectable="true"
      :current-page="currentPage"
      :per-page="perPage"
      :total-rows="totalRows"
      :tableOptions="tableOptions"
      :pageOptions="[10, 25, 50, 100]"
      @sort-changed="onSortChanged"
      @row-selected="handleEvent"
      @filtered="onFilterChanged"
      @page-changed="onPageChanged"
      @per-page-changed="onPerPageChanged"
    >
      <template #empty>
        <h4 class="text-secondary text-center mt-1">Aucune alarme présente</h4>
      </template>
      <template #cell(datetime)="data">
        {{ formatDate(data.item.datetime) }}
      </template>
      <template #cell(datetimeAcq)="data">
        {{ formatDate(data.item.datetimeAcq) }}
      </template>
      <template v-slot:cell(actions)="data">
        <div class="text-center">
          <b-button
            v-if="!data.item.datetimeAcq"
            class="Tenants-new-button"
            variant="outline-primary"
            :data-cy="`view-residence-content-${data.item._id}`"
            @click="(show = true), (ticket = data)"
            >&nbsp;Acquitter</b-button
          >
          <i
            class="fas fa-check"
            v-if="data.item.datetimeAcq"
            title="Cette alerte a bien été acqittée"
            :data-cy="`view-residence-content-${data.item._id}`"
          />
          &nbsp;
        </div>
      </template>
    </CustomTable>
    <b-modal :id="systemId" v-model="show" size="l" :hide-header="true">
      <p v-if="ticket && ticket.item.alarmId">
        Voulez-vous acquitter le ticket <code>{{ ticket.item.alarmId }}</code>
      </p>
      <template #modal-footer>
        <div class="w-100">
          <b-button
            variant="primary"
            class="float-right mr-3"
            @click="show = false"
          >
            Non
          </b-button>
          <b-button
            variant="primary"
            class="float-right mr-3"
            @click="acknowledgeTicket"
          >
            Oui
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import 'ace-builds';
import 'ace-builds/webpack-resolver';
import CustomTable from '@/components/CustomTable.vue';
import { EVENT_NAME } from '@/types/Alarm.ts'

export default {
  name: 'TechnicalAlertsTableView',
  components: {
    CustomTable,
  },
  props: {
    index: {
      type: String,
      required: true,
    },
    collection: {
      type: String,
      required: true,
    },
    systemId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      show: false,
      ticket: null,
      fromDate: '',
      toDate: '',
      filter: '',
      sort: null,
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      mapping: null,
      items: [],
    };
  },
  computed: {
    fromDateFormatted() {
      return this.fromDate ? `${this.fromDate} 00:00:00` : '';
    },
    toDateFormatted() {
      return this.toDate ? `${this.toDate} 23:59:59` : '';
    },
  },
  methods: {
    handleEvent(data) {
      this.$log.debug(data);
    },
    onPageChanged(page) {
      this.currentPage = page;
      this.fetchItems();
    },
    onPerPageChanged(value) {
      this.perPage = value;
      this.currentPage = 1;
      this.fetchItems();
    },
    onFilterChanged(receivedFilter) {
      this.filter = receivedFilter;
      this.fetchItems();
    },
    onSortChanged(data) {
      this.sort = [
        {
          [data.sortBy]: {
            order: data.sortDesc ? 'desc' : 'asc',
          },
        },
      ];
      this.fetchItems();
    },
    formatDate(dateString) {
      try {
        return this.$d(new Date(dateString), 'long');
      } catch {
        return 'N/A';
      }
    },
    async acknowledgeTicket() {
      try {
        await this.$kuzzle.query({
          index: this.index,
          controller: 'pvstudio',
          action: 'ackAlarm',
          alarmId: this.ticket.item._id,
        });

        this.$emit('ackAlarm', this.ticket.item._id);
        this.fetchItems();
        this.show = false;
      } catch (error) {
        this.$log.error(error);
        this.$bvToast.toast(
          "Désolé, une erreur est survenue lors de l'acquittement de l'alarme.",
          {
            title: 'Erreur',
            autoHideDelay: 5000,
            variant: 'danger',
            appendToast: true,
          }
        );

        this.fetchItems();
      }
    },
    async fetchItems() {
      try {
        let query = {
          query: {
            bool: {
              must: [
                {
                  match: {
                    systemId: this.systemId,
                  },
                },
              ],
            },
          },
        };
        if (this.fromDateFormatted && this.toDateFormatted) {
          query.query.bool.must.push({
            range: {
              datetime: {
                gt: this.fromDateFormatted,
                lt: this.toDateFormatted,
              },
            },
          });
        }
        if (this.filter.length) {
          query.query.bool.must.push({
            multi_match: {
              query: this.filter,
              type: 'phrase_prefix',
              fields: ['*'],
            },
          });
        }
        if (this.sort) {
          const sorts = [];
          for (const v of this.sort) {
            for (const k of Object.keys(v)) {
              if (k === 'datetime' || k === 'datetimeAcq') {
                sorts.push({ [k]: v[k] });
              } else {
                sorts.push({ [`${k}.keyword`]: v[k] });
              }
            }
          }
          query.sort = sorts;
        } else {
          query.sort = {
            datetime: 'desc',
          };
        }

        const res = await this.$kuzzle.document.query({
          controller: 'pvstudio',
          action: 'technicalAlarms',
          index: this.index,
          collection: this.collection,
          query,
          from: (this.currentPage - 1) * this.perPage,
          size: this.perPage,
        });

        this.items.splice(this.items, this.items.length);
        for (const doc of res.result.hits) {
          this.items.push({
            _id: doc._id,
            ...doc._source,
            eventName: EVENT_NAME[doc._source.eventId] ? this.$i18n.t(`alarm.${EVENT_NAME[doc._source.eventId]}`) : doc._source.eventName,
          });
        }
        this.totalRows = res.result.total;
      } catch (error) {
        this.$log.error(error);
        this.$bvToast.toast(
          'Désolé, une erreur est survenue lors de la récupération des alarmes techniques.',
          {
            title: 'Erreur',
            autoHideDelay: 5000,
            variant: 'danger',
            appendToast: true,
          }
        );
      }
    },
  },
  created() {
    // Here, we declare the constant data used in the template
    this.fields = [
      {
        key: 'alarmId',
        label: "N d'alarme",
        sortable: true,
        thClass: 'bg-success text-light',
      },
      {
        key: 'datetime',
        label: 'Date/Heure de déclenchement',
        sortable: true,
        thClass: 'bg-success text-light',
      },
      {
        key: 'sourceUserName',
        label: 'Utilisateur',
        sortable: true,
        thClass: 'bg-success text-light',
      },
      {
        key: 'equipmentTypeName',
        label: 'Type de declencheur',
        sortable: true,
        thClass: 'bg-success text-light',
      },
      {
        key: 'alarmReasonName',
        label: 'Cause',
        sortable: true,
        thClass: 'bg-success text-light',
      },
      {
        key: 'alarmReasonId',
        label: 'alarme ID',
        sortable: true,
        thClass: 'bg-success text-light',
      },
      {
        key: 'positionName',
        label: 'Position',
        sortable: true,
        thClass: 'bg-success text-light',
      },
      {
        key: 'handledByPersonnelName',
        label: 'Intervenant',
        sortable: true,
        thClass: 'bg-success text-light',
      },
      {
        key: 'datetimeAcq',
        label: "Date/Heure de l'acquittement",
        sortable: true,
        thClass: 'bg-success text-light',
      },
      {
        key: 'eventName',
        label: 'Statut',
        sortable: true,
        thClass: 'bg-success text-light',
      },
      {
        key: '',
        label: 'Motif',
        sortable: true,
        thClass: 'bg-success text-light',
      },
      {
        key: 'actions',
        label: '',
        sortable: true,
        thClass: 'bg-success text-light',
      },
    ];
    this.tableOptions = {
      'head-variant': 'light',
    };
  },
  async mounted() {
    await this.fetchItems();
  },
  watch: {
    async systemId() {
      await this.fetchItems();
    },
    async fromDate() {
      await this.fetchItems();
    },
    async toDate() {
      await this.fetchItems();
    },
  },
};
</script>

<style lang="scss" scoped>
.pointer {
  cursor: pointer;
}
</style>

<style lang="scss">
.table {
  color: $primary;
}

.b-table-sticky-header {
  max-height: 60vh !important;
}
</style>
